/**
 * Whether this page load is a guest opening a menu from a printed QR code, and
 * with which token.
 *
 * Decided before anything renders, in `main.tsx`, because the answer picks a
 * different app: no sign-in, no back office, none of its code downloaded. So
 * it is strict about the shape — exactly `/m/<something>`, a trailing slash
 * forgiven because some scanners add one — and anything else is the back
 * office's to answer. What the something is, the server decides: a mangled
 * token still lands a guest on the guest page, which says the link no longer
 * works, rather than on a staff sign-in.
 */
const GUEST_MENU = /^\/m\/([^/]+)\/?$/;
/** On the menu's own host the token is the whole path: `menu.<domain>/<token>`. */
const MENU_HOST_PATH = /^\/([^/]*)\/?$/;

/**
 * The host a printed code opens since the menu has a subdomain of its own.
 * Everything there is a guest menu: the back office never loads on it, so a
 * path that is not a token is a link that does not work, said as one.
 */
export function isMenuHost(hostname: string): boolean {
  return hostname.startsWith('menu.');
}

export function guestMenuToken(pathname: string, hostname = ''): string | null {
  if (isMenuHost(hostname)) {
    const onHost = MENU_HOST_PATH.exec(pathname) ?? GUEST_MENU.exec(pathname);
    return decode(onHost?.[1] ?? '');
  }
  const match = GUEST_MENU.exec(pathname);
  if (!match) return null;
  return decode(match[1]!);
}

function decode(token: string): string {
  try {
    return decodeURIComponent(token);
  } catch {
    // A stray `%` from a scanner that mangled the link. Still a guest; the
    // server will say this one does not open anything.
    return token;
  }
}

/**
 * Where each category's heading is, for the jump links along the top. From
 * its position, not its name: two categories can share a first word, and a
 * guest's language can be anything a URL fragment makes awkward.
 */
export function categoryAnchor(index: number): string {
  return `c${index + 1}`;
}

/** Jump links earn their room from this many categories; below it, scrolling is quicker. */
export const JUMP_LINKS_FROM = 4;
